import { paintedAccountLast4s } from '@/modules/projects/components/StatementCoverage/deriveCoverage'
import type {
  DirectoryAccount,
  FilePage,
  Statement as ProjectFile,
  SegmentRangeDTO,
  StatementSegment,
} from '@/modules/projects/stores/types'
import { arrangementFrom } from './assignment'
import {
  ACCOUNT_TYPES,
  type Account,
  type AccountType,
  type Arrangement,
  type DatePeriod,
  type DocumentPage,
  type PageGroup,
  type PageSource,
  type SourceDocument,
  type StatementView,
} from './types'

const SEGMENT_PREFIX = 'seg:'
const UNFILED_PREFIX = 'unfiled:'

// A lukita statement is an upload; a statement on this screen is one of its cycles. The pile of
// pages no cycle claims is not a cycle, so it is named after the file it sits in.
export const unfiledPileId = (fileId: number) => `${UNFILED_PREFIX}${fileId}`

export const isUnfiledPile = (statementId: string) =>
  statementId.startsWith(UNFILED_PREFIX)

export const statementIdOfSegment = (segmentId: number) =>
  `${SEGMENT_PREFIX}${segmentId}`

export const segmentIdOf = (statementId: string) =>
  statementId.startsWith(SEGMENT_PREFIX)
    ? Number(statementId.slice(SEGMENT_PREFIX.length))
    : null

export interface ProjectSplit {
  document: SourceDocument
  arrangement: Arrangement
  // The cycle behind each filed statement, for its verdict, its badge and every write back.
  segments: ReadonlyMap<string, StatementSegment>
  // The upload each cycle was cut from. Pages can be staged into another file, so this is the
  // only thing that still names the file the cycle really belongs to.
  fileOf: ReadonlyMap<string, number>
}

const periodOf = (segment: StatementSegment): DatePeriod | null =>
  segment.startDate && segment.endDate
    ? { start: segment.startDate, end: segment.endDate }
    : null

// lukita keeps the type as an upcased enum key, the editor as the lower-case word.
const accountTypeOf = (entry: DirectoryAccount | undefined) => {
  const word = entry?.accountType?.toLowerCase()
  return ACCOUNT_TYPES.includes(word as AccountType)
    ? (word as AccountType)
    : null
}

// A cycle is filed under the accounts it was reconciled to, the same rule the coverage page paints
// by. A number merely printed on the page (a payment from another card) must not open a second lane.
const last4sOf = (segment: StatementSegment) =>
  [...paintedAccountLast4s(segment)].sort()

// A combined mailing carries several numbers, so the lane is named by all of them at once and its
// pages are never split across two lanes.
const laneIdOf = (numbers: string[]) => numbers.join('+')

const BLANK_PAGE = 'blank'

// Kroos reads no text off a blank sheet and cuts only the sheets it read, so a sheet after the
// file's last cycle was never a candidate for one. Older runs did not report those sheets, so a
// trailing sheet no range explains is read the same way. Nobody can file a blank sheet, so the
// editor does not show one.
const hiddenSheets = (file: ProjectFile) => {
  const classOfSheet = new Map<number, string>()
  for (const range of file.excludedRanges ?? []) {
    for (let sheet = range.startPage; sheet <= range.endPage; sheet += 1) {
      classOfSheet.set(sheet, range.documentClass)
    }
  }

  const hidden = new Set<number>()
  for (const [sheet, documentClass] of classOfSheet) {
    if (documentClass === BLANK_PAGE) hidden.add(sheet)
  }
  const lastCut = Math.max(
    0,
    ...(file.segments ?? []).map((segment) => segment.endPage ?? 0),
  )
  for (
    let sheet = lastCut + 1;
    lastCut > 0 && sheet <= file.numberOfPages;
    sheet += 1
  ) {
    if (!classOfSheet.has(sheet)) hidden.add(sheet)
  }
  return hidden
}

const sheetKey = (fileId: number, sheet: number) => `${fileId}:${sheet}`

// Sheets a statement lists by hand. Such a sheet is filed, so it is never a blank tail, even past
// the last range its file's cycles cover.
const listedSheets = (files: ProjectFile[]) =>
  new Set(
    files.flatMap((file) =>
      (file.segments ?? []).flatMap((segment) =>
        (segment.pages ?? []).map(([fileId, sheet]) => sheetKey(fileId, sheet)),
      ),
    ),
  )

// A statement with a saved page order is laid down whole at its first sheet in its own upload, so a
// page moved in from another upload reads in place. Listed sheets are skipped where their files hold them.
const readingOrder = (files: ProjectFile[], listed: ReadonlySet<string>) => {
  const laidAt = new Map<string, FilePage[]>()
  for (const file of files) {
    for (const segment of file.segments ?? []) {
      if (!segment.pages?.length) continue
      const [fileId, sheet] =
        segment.pages.find(([id]) => id === file.id) ?? segment.pages[0]
      laidAt.set(sheetKey(fileId, sheet), segment.pages)
    }
  }

  return files.flatMap((file) =>
    Array.from({ length: file.numberOfPages }, (_, index): FilePage[] => {
      const key = sheetKey(file.id, index + 1)
      if (!listed.has(key)) return [[file.id, index + 1]]
      return laidAt.get(key) ?? []
    }).flat(),
  )
}

const pagesOfFiles = (files: ProjectFile[]) => {
  const pages: DocumentPage[] = []
  const numberOfSheet = new Map<string, number>()
  const fileById = new Map(files.map((file) => [file.id, file]))
  const hiddenByFile = new Map(
    files.map((file) => [file.id, hiddenSheets(file)]),
  )
  const listed = listedSheets(files)
  const hidden = (fileId: number, sheet: number) =>
    !listed.has(sheetKey(fileId, sheet)) &&
    Boolean(hiddenByFile.get(fileId)?.has(sheet))

  for (const [fileId, sheet] of readingOrder(files, listed)) {
    const file = fileById.get(fileId)
    const key = sheetKey(fileId, sheet)
    if (!file || hidden(fileId, sheet) || numberOfSheet.has(key)) continue
    numberOfSheet.set(key, pages.length + 1)
    pages.push({
      number: pages.length + 1,
      source: {
        fileId: file.id,
        fileUrl: file.fileUrl,
        fileName: file.fileName,
        pageInFile: sheet,
      },
    })
  }

  return { pages, numberOfSheet }
}

const sheetsOfSegment = (
  segment: StatementSegment,
  fileId: number,
): FilePage[] => {
  if (segment.pages) return segment.pages
  const { startPage, endPage } = segment
  if (startPage === null || endPage === null) return []
  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index): FilePage => [fileId, startPage + index],
  )
}

interface SegmentGroup {
  group: PageGroup
  segment: StatementSegment
  fileId: number
}

// Every cycle that names its pages, laid on the project-wide page numbers. A page two cycles both
// claim goes to the later one, which is the reading the user is most likely correcting. A saved
// page list beats a range: it is where a page moved by hand now lives.
const segmentGroups = (
  files: ProjectFile[],
  numberOfSheet: ReadonlyMap<string, number>,
) => {
  const claimed: SegmentGroup[] = []
  const ownerByPage = new Map<number, string>()

  const claim = (segment: StatementSegment, fileId: number) => {
    const id = statementIdOfSegment(segment.id)
    for (const [sheetFileId, sheet] of sheetsOfSegment(segment, fileId)) {
      const page = numberOfSheet.get(sheetKey(sheetFileId, sheet))
      if (page !== undefined) ownerByPage.set(page, id)
    }
  }

  for (const file of files) {
    for (const segment of file.segments ?? []) {
      if (sheetsOfSegment(segment, file.id).length === 0) continue
      const id = statementIdOfSegment(segment.id)
      if (!segment.pages) claim(segment, file.id)

      claimed.push({
        segment,
        fileId: file.id,
        group: {
          id,
          pages: [],
          period: periodOf(segment),
          accountId: laneIdOf(last4sOf(segment)) || null,
        },
      })
    }
  }

  for (const { segment, fileId } of claimed) {
    if (segment.pages) claim(segment, fileId)
  }

  for (const { group } of claimed) {
    group.pages = [...ownerByPage]
      .filter(([, owner]) => owner === group.id)
      .map(([page]) => page)
      .sort((one, other) => one - other)
  }

  return { claimed, ownerByPage }
}

export const pageNumbersByFile = (pages: DocumentPage[]) => {
  const byFile = new Map<number, number[]>()
  for (const page of pages) {
    const { fileId } = page.source
    byFile.set(fileId, [...(byFile.get(fileId) ?? []), page.number])
  }
  return byFile
}

const unfiledGroups = (
  pages: DocumentPage[],
  ownerByPage: ReadonlyMap<number, string>,
) => {
  const looseByFile = pageNumbersByFile(
    pages.filter((page) => !ownerByPage.has(page.number)),
  )

  return [...looseByFile].map(([fileId, loose]) => ({
    id: unfiledPileId(fileId),
    pages: loose,
    period: null,
    accountId: null,
  }))
}

// The directory's facts for an account, taken from its first number. Numbers of different types
// on one mailing leave the type unknown rather than picking one.
export const accountOfNumbers = (
  id: string,
  numbers: string[],
  directory: DirectoryAccount[],
): Account => {
  const entryFor = (last4: string) =>
    directory.find((entry) => entry.last4 === last4)
  const first = entryFor(numbers[0])
  const accountType = accountTypeOf(first)
  const mixedTypes = numbers.some(
    (number) => accountTypeOf(entryFor(number)) !== accountType,
  )
  return {
    id,
    numbers,
    accountType: mixedTypes ? null : accountType,
    owner: first?.owner ?? null,
    institution: first?.institution ?? null,
  }
}

// One account per set of numbers a cycle is filed under, one per number inside those sets, plus
// an empty one for every account a person added and has not filed under yet. An account the reading
// found with nothing filed under it is a stray number, such as a check's trace number, so it is left out.
const accountsFor = (
  groups: PageGroup[],
  directory: DirectoryAccount[],
): Account[] => {
  const lanes = new Map<string, string[]>()
  for (const group of groups) {
    if (group.accountId) lanes.set(group.accountId, group.accountId.split('+'))
  }

  // A number a combined mailing names is an account of its own, with its own lane, so the
  // statement shows in each account it covers, the way the coverage page paints it.
  const filedNumbers = new Set([...lanes.values()].flat())
  for (const number of filedNumbers) {
    if (!lanes.has(number)) lanes.set(number, [number])
  }
  for (const entry of directory) {
    // A row with no number is not an account anyone can file under, and its empty lane id would
    // collide with the lane for statements that print no number at all.
    if (!entry.last4 || filedNumbers.has(entry.last4)) continue
    if (entry.addedByHand) lanes.set(entry.last4, [entry.last4])
  }

  return [...lanes]
    .sort(([one], [other]) => one.localeCompare(other))
    .map(([id, numbers]) => accountOfNumbers(id, numbers, directory))
}

// The project as lukita filed it, read as a document the editor can rearrange.
export const splitFromProject = (
  unorderedFiles: ProjectFile[],
  directory: DirectoryAccount[],
): ProjectSplit => {
  // The store lists files by last update, which every edit reshuffles; page numbers must not move.
  const files = [...unorderedFiles].sort((one, other) => one.id - other.id)
  const { pages, numberOfSheet } = pagesOfFiles(files)
  const { claimed, ownerByPage } = segmentGroups(files, numberOfSheet)
  const filedGroups = [
    ...claimed.map(({ group }) => group),
    ...unfiledGroups(pages, ownerByPage),
  ]
  const accounts = accountsFor(filedGroups, directory)

  return {
    document: { pages },
    arrangement: arrangementFrom(accounts, filedGroups),
    segments: new Map<string, StatementSegment>(
      claimed.map(({ group, segment }) => [group.id, segment]),
    ),
    fileOf: new Map<string, number>(
      claimed.map(({ group, fileId }) => [group.id, fileId]),
    ),
  }
}

export interface FileRecut {
  fileId: number
  ranges: SegmentRangeDTO[]
}

export interface CommitPlan {
  recuts: FileRecut[]
  // Statements the server cannot be asked to cut, named so the user can put them right first.
  blocked: string[]
}

// Which uploads have to be cut again: the ones holding a page that changed hands.
export const touchedFiles = (
  sourceDocument: SourceDocument,
  before: Arrangement,
  after: Arrangement,
) => {
  const files = new Set<number>()
  for (const page of sourceDocument.pages) {
    const moved =
      before.ownerByPage.get(page.number) !== after.ownerByPage.get(page.number)
    if (moved) files.add(page.source.fileId)
  }
  return files
}

export const sheetsOf = (
  pages: number[],
  pageAt: (page: number) => DocumentPage | null,
) =>
  pages.flatMap((page) => {
    const held = pageAt(page)
    return held ? [held.source] : []
  })

const sheetNumbers = (sources: PageSource[]) =>
  sources.map((source) => source.pageInFile).sort((one, other) => one - other)

// lukita cuts a cycle as one page range of one upload, so only such a statement can be sent.
export const isOneRun = (sources: PageSource[]) => {
  const sheets = sheetNumbers(sources)
  return (
    new Set(sources.map((source) => source.fileId)).size === 1 &&
    sheets[sheets.length - 1] - sheets[0] + 1 === sheets.length
  )
}

// A cycle is one run of sheets inside one upload. Anything else is not something lukita can cut,
// so it is named and nothing is sent. The account is not sent: a re-read cycle reads it off its pages.
export const planRecuts = (
  views: StatementView[],
  files: ReadonlySet<number>,
  pageAt: (page: number) => DocumentPage | null,
  nameOf: (view: StatementView) => string,
): CommitPlan => {
  // Every touched file is sent, even one left holding no statement at all: its pages have to
  // stop being part of one.
  const ranges = new Map<number, SegmentRangeDTO[]>(
    [...files].map((fileId) => [fileId, []]),
  )
  const blocked: string[] = []

  for (const view of views) {
    if (view.pages.length === 0 || isUnfiledPile(view.statement.id)) continue
    const sources = sheetsOf(view.pages, pageAt)
    const fileIds = new Set(sources.map((source) => source.fileId))
    if (![...fileIds].some((fileId) => files.has(fileId))) continue

    if (!isOneRun(sources)) {
      blocked.push(nameOf(view))
      continue
    }

    const sheets = sheetNumbers(sources)
    const fileId = sources[0].fileId
    ranges.set(fileId, [
      ...(ranges.get(fileId) ?? []),
      {
        start_page: sheets[0],
        end_page: sheets[sheets.length - 1],
        start_date: view.statement.period?.start ?? null,
        end_date: view.statement.period?.end ?? null,
      },
    ])
  }

  return {
    recuts: [...ranges].map(([fileId, cuts]) => ({
      fileId,
      ranges: [...cuts].sort((one, other) => one.start_page - other.start_page),
    })),
    blocked,
  }
}
